/**
 * Giro 2.5D de la cabeza.
 *
 * La foto no se puede girar como un objeto 3D, pero casi: con un mapa de
 * profundidad (tools/bake_depth.py) cada pixel se desplaza en proporcion a lo
 * cerca que esta de la camara. Al girar a un lado, la nariz se mueve mas que
 * las mejillas y el contorno no se mueve: eso es lo que el ojo lee como giro,
 * y no como una foto que se desliza. Los hombros tienen peso cero y no giran.
 *
 * Se hace en WebGL (un sombreador de una pasada, ~1 ms por fotograma). Si el
 * navegador no tiene WebGL, `create` devuelve null y el avatar sigue igual que
 * antes, sin giro.
 */

const VERT = `
attribute vec2 aPos;
varying vec2 vUv;
void main() {
  vUv = vec2(aPos.x * 0.5 + 0.5, 0.5 - aPos.y * 0.5);
  gl_Position = vec4(aPos, 0.0, 1.0);
}`;

// Mapeo inverso: para cada pixel de salida se busca de donde viene. Dos
// iteraciones de punto fijo bastan para giros de unos grados.
const FRAG = `
precision mediump float;
uniform sampler2D uFace;
uniform sampler2D uDepth;
uniform vec2 uShift;
varying vec2 vUv;
vec2 disp(vec2 p) {
  vec4 d = texture2D(uDepth, p);
  return uShift * d.r * d.g;
}
void main() {
  vec2 s = vUv - disp(vUv);
  s = vUv - disp(s);
  gl_FragColor = texture2D(uFace, s);
}`;

export interface HeadWarp {
  canvas: HTMLCanvasElement;
  /**
   * Pinta `face` girada. yaw > 0 gira hacia la derecha de la imagen, pitch > 0
   * baja la cara (asentir). En grados.
   */
  render(face: HTMLCanvasElement, yawDeg: number, pitchDeg: number): void;
}

export function createHeadWarp(width: number, height: number, depth: HTMLImageElement, depthMax: number): HeadWarp | null {
  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  const gl = canvas.getContext('webgl', { premultipliedAlpha: true, preserveDrawingBuffer: true, antialias: false });
  if (!gl) return null;

  const shader = (type: number, src: string) => {
    const s = gl.createShader(type)!;
    gl.shaderSource(s, src);
    gl.compileShader(s);
    if (!gl.getShaderParameter(s, gl.COMPILE_STATUS)) throw new Error(gl.getShaderInfoLog(s) ?? 'shader');
    return s;
  };
  let program: WebGLProgram;
  try {
    program = gl.createProgram()!;
    gl.attachShader(program, shader(gl.VERTEX_SHADER, VERT));
    gl.attachShader(program, shader(gl.FRAGMENT_SHADER, FRAG));
    gl.linkProgram(program);
    if (!gl.getProgramParameter(program, gl.LINK_STATUS)) return null;
  } catch {
    return null;
  }
  gl.useProgram(program);

  const buf = gl.createBuffer();
  gl.bindBuffer(gl.ARRAY_BUFFER, buf);
  gl.bufferData(gl.ARRAY_BUFFER, new Float32Array([-1, -1, 1, -1, -1, 1, 1, 1]), gl.STATIC_DRAW);
  const aPos = gl.getAttribLocation(program, 'aPos');
  gl.enableVertexAttribArray(aPos);
  gl.vertexAttribPointer(aPos, 2, gl.FLOAT, false, 0, 0);

  const texture = (unit: number) => {
    const t = gl.createTexture()!;
    gl.activeTexture(gl.TEXTURE0 + unit);
    gl.bindTexture(gl.TEXTURE_2D, t);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);
    return t;
  };
  const faceTex = texture(0);
  const depthTex = texture(1);
  // La profundidad son datos, no color: sin premultiplicar ni corregir.
  gl.pixelStorei(gl.UNPACK_PREMULTIPLY_ALPHA_WEBGL, false);
  gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, gl.RGBA, gl.UNSIGNED_BYTE, depth);
  gl.uniform1i(gl.getUniformLocation(program, 'uFace'), 0);
  gl.uniform1i(gl.getUniformLocation(program, 'uDepth'), 1);
  const uShift = gl.getUniformLocation(program, 'uShift');
  gl.viewport(0, 0, width, height);

  return {
    canvas,
    render(face, yawDeg, pitchDeg) {
      gl.activeTexture(gl.TEXTURE0);
      gl.bindTexture(gl.TEXTURE_2D, faceTex);
      gl.pixelStorei(gl.UNPACK_PREMULTIPLY_ALPHA_WEBGL, true);
      gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, gl.RGBA, gl.UNSIGNED_BYTE, face);
      // Desplazamiento maximo (en fraccion de la imagen) para profundidad 1.
      const k = Math.PI / 180;
      gl.uniform2f(uShift, (Math.sin(yawDeg * k) * depthMax) / width, (Math.sin(pitchDeg * k) * depthMax) / height);
      gl.clearColor(0, 0, 0, 0);
      gl.clear(gl.COLOR_BUFFER_BIT);
      gl.drawArrays(gl.TRIANGLE_STRIP, 0, 4);
    },
  };
}
